import {
  useGetDashboard,
  useUpdateDashboard,
  getGetDashboardQueryKey,
  getListDashboardsQueryKey,
} from '@smarthome/api';
import { ROUTES } from '@smarthome/ui';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, LayoutDashboard, AlertCircle, Monitor } from 'lucide-react';
import { useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';

import { Button, useToast } from '@/components/ui';
import { DashboardEditor } from '@/features/dashboards/editor';
import type { EditorWidget } from '@/features/dashboards/types';

export function DashboardEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const { data, isLoading, isError, refetch } = useGetDashboard(id ?? '', {
    query: {
      enabled: !!id,
    },
  });

  const dashboard = data?.dashboard;

  const { mutate: updateDashboard, isPending: isSaving } = useUpdateDashboard({
    mutation: {
      onSuccess: () => {
        addToast({ message: 'Dashboard saved', variant: 'success' });
        if (id) {
          queryClient.invalidateQueries({ queryKey: getGetDashboardQueryKey(id) });
        }
        queryClient.invalidateQueries({ queryKey: getListDashboardsQueryKey() });
      },
      onError: (err) => {
        addToast({
          message: err instanceof Error ? err.message : 'Failed to save dashboard',
          variant: 'error',
        });
      },
    },
  });

  const handleSave = useCallback(
    (document: { schemaVersion: number; timeRange?: string; widgets: EditorWidget[] }) => {
      if (!id || !dashboard?.name) return;
      updateDashboard({
        id,
        data: {
          name: dashboard.name,
          description: dashboard.description || undefined,
          document: {
            schemaVersion: document.schemaVersion,
            timeRange: document.timeRange,
            widgets: document.widgets.map((w) => ({
              id: w.id,
              type: w.type,
              title: w.title,
              x: w.x,
              y: w.y,
              w: w.w,
              h: w.h,
              config: w.config,
            })),
          },
        },
      });
    },
    [id, dashboard, updateDashboard]
  );

  const handleCancel = useCallback(() => {
    if (id) {
      navigate(ROUTES.DASHBOARD_DETAIL.replace(':id', id));
    } else {
      navigate(ROUTES.DASHBOARDS);
    }
  }, [id, navigate]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
      </div>
    );
  }

  if (isError || !dashboard) {
    return (
      <div className="space-y-6">
        <div>
          <Link
            to={ROUTES.DASHBOARDS}
            className="inline-flex items-center gap-2 text-sm text-gray-500 transition-colors hover:text-gray-900 dark:text-zinc-500 dark:hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to dashboards
          </Link>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-6 text-center dark:border-white/[0.06] dark:bg-[#18181b]">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-red-500/10">
            <AlertCircle className="h-6 w-6 text-red-400" />
          </div>
          <p className="text-sm text-gray-600 dark:text-zinc-400">Failed to load dashboard</p>
          <button
            onClick={() => refetch()}
            className="mt-3 text-sm text-blue-500 hover:text-blue-400"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  // Check if not editable
  if (!dashboard.isEditable) {
    return (
      <div className="space-y-6">
        <div>
          <Link
            to={id ? ROUTES.DASHBOARD_DETAIL.replace(':id', id) : ROUTES.DASHBOARDS}
            className="inline-flex items-center gap-2 text-sm text-gray-500 transition-colors hover:text-gray-900 dark:text-zinc-500 dark:hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to dashboard
          </Link>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-6 text-center dark:border-white/[0.06] dark:bg-[#18181b]">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-amber-500/10">
            <AlertCircle className="h-6 w-6 text-amber-400" />
          </div>
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">View Only Dashboard</h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-zinc-400">
            This dashboard was shared with you in view-only mode. You cannot edit it.
          </p>
          <Link to={id ? ROUTES.DASHBOARD_DETAIL.replace(':id', id) : ROUTES.DASHBOARDS}>
            <Button className="mt-4" variant="outline">
              View Dashboard
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const backUrl = id ? ROUTES.DASHBOARD_DETAIL.replace(':id', id) : ROUTES.DASHBOARDS;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Link
          to={backUrl}
          className="mb-3 inline-flex items-center gap-2 text-sm text-gray-500 transition-colors hover:text-gray-900 dark:text-zinc-500 dark:hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to dashboard
        </Link>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400">
            <LayoutDashboard className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
              Edit: {dashboard.name}
            </h1>
            {dashboard.description && (
              <p className="mt-0.5 text-sm text-gray-500 dark:text-zinc-500">
                {dashboard.description}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Small screen warning */}
      <div className="block lg:hidden">
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-center dark:border-amber-500/20 dark:bg-amber-500/10">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-500/20">
            <Monitor className="h-6 w-6 text-amber-600 dark:text-amber-400" />
          </div>
          <h2 className="text-lg font-medium text-amber-800 dark:text-amber-300">
            Desktop Required
          </h2>
          <p className="mt-2 text-sm text-amber-700 dark:text-amber-400">
            The dashboard editor is optimized for larger screens. Please use a desktop or laptop
            computer for the best editing experience.
          </p>
          <Link to={backUrl}>
            <Button className="mt-4" variant="outline">
              View Dashboard Instead
            </Button>
          </Link>
        </div>
      </div>

      {/* Desktop editor */}
      <div className="hidden lg:block">
        <DashboardEditor
          dashboard={dashboard}
          onSave={handleSave}
          onCancel={handleCancel}
          isSaving={isSaving}
        />
      </div>
    </div>
  );
}
